/**
 * Types, libellés et bornes du moteur d'envoi de la prospection (migration
 * 021), partagés entre le serveur et les composants client. Comme
 * lib/prospects-shared.ts, ce module n'importe aucune dépendance serveur.
 */

// ---------------------------------------------------------------------------
// Réglages
// ---------------------------------------------------------------------------

export type ProspectionSettings = {
  /** Préparation des dossiers et lecture des boîtes. */
  machineEnabled: boolean;
  /** Interrupteur général des envois, relances comprises. */
  sendingEnabled: boolean;
  /** Un e-mail qui passe le garde-fou part sans clic « Valider ». */
  autoApprove: boolean;
  /** Un « oui » net reçoit automatiquement la source et les autres signaux. */
  autoReplyYes: boolean;
  /** Dossiers préparés au maximum par tranche de 24 heures. */
  dailyQuota: number;
  /** Jours avant qu'une même personne puisse recevoir une nouvelle séquence. */
  cooldownDays: number;
  /** Jours d'envoi ISO (1 = lundi). */
  sendDays: number[];
  sendWindowStart: string;
  sendWindowEnd: string;
  inboxIntervalMinutes: number;
  inboxStart: string;
  inboxEnd: string;
  dailyCapPerMailbox: number;
  minGapMinutes: number;
  maxGapMinutes: number;
  /** Nombre de relances après le premier e-mail (0 à 3). */
  followups: number;
  /** Taux de rebond maximal sur 7 jours, en points de base (300 = 3 %). */
  maxBounceRateBps: number;
  maxUnsubscribesPer100: number;
  /** La ligne sous le nom de la boîte (sa fonction, par exemple). */
  signature: string;
  /** Signature « comme dans Gmail » : logo Hune, lien vers le site et promesse (partie HTML). */
  signatureRich: boolean;
  /** Échanges de chauffe maintenus après la cinquième semaine (3 par jour et par boîte). */
  warmupContinuous: boolean;
  /** Jours de chauffe seule avant les premiers e-mails de prospection (0 à 14). */
  warmupDays: number;
  /** Adresse postale du pied d'e-mail ; vide = celle de l'éditeur. */
  postalAddress: string;
  /** Cadre commun des e-mails ; vide = brief par défaut. */
  brief: string;
  /** Modèle qui rédige les e-mails, une fois le dossier rassemblé. */
  writerModel: WriterModel;
};

/** Modèles proposés pour rédiger les e-mails de prospection. */
export const WRITER_MODELS = [
  { value: "deepseek-v4-pro", label: "DeepSeek V4 Pro — plus juste, un peu plus lent" },
  { value: "deepseek-flash", label: "DeepSeek Flash — rapide, moins fin" },
] as const;
export type WriterModel = (typeof WRITER_MODELS)[number]["value"];
export const DEFAULT_WRITER_MODEL: WriterModel = "deepseek-v4-pro";

export function isWriterModel(value: unknown): value is WriterModel {
  return WRITER_MODELS.some((model) => model.value === value);
}

export const PROSPECTION_LIMITS = {
  dailyQuota: { min: 1, max: 25, default: 5 },
  cooldownDays: { min: 7, max: 365, default: 60 },
  dailyCapPerMailbox: { min: 1, max: 40, default: 30 },
  minGapMinutes: { min: 2, max: 60, default: 4 },
  maxGapMinutes: { min: 2, max: 120, default: 11 },
  followups: { min: 0, max: 3, default: 3 },
  inboxIntervalMinutes: { min: 10, max: 240, default: 60 },
  maxBounceRateBps: { min: 50, max: 1000, default: 300 },
  maxUnsubscribesPer100: { min: 1, max: 10, default: 2 },
  warmupDays: { min: 0, max: 14, default: 14 },
} as const;

export function clampSetting(key: keyof typeof PROSPECTION_LIMITS, value: number) {
  const { min, max, default: fallback } = PROSPECTION_LIMITS[key];
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}

export const CLOCK_PATTERN = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

export const WEEKDAY_LABELS: Record<number, string> = {
  1: "lun.",
  2: "mar.",
  3: "mer.",
  4: "jeu.",
  5: "ven.",
  6: "sam.",
  7: "dim.",
};

/**
 * Brief par défaut des e-mails de prospection, appliqué tant que
 * l'administration n'en a pas enregistré un autre.
 */
/**
 * Ce que fait Hune, tel que les e-mails le présentent. Fait foi pour le
 * rédacteur comme pour le relecteur, quel que soit le brief des réglages :
 * sinon le relecteur retient comme inventé ce que le rédacteur doit dire.
 */
export const HUNE_FACTS = [
  "Chaque semaine, Hune trouve pour une équipe commerciale des entreprises (ou des organismes) qu'elle pourrait démarcher, au moment où un fait public et daté leur donne une vraie raison d'en parler.",
  "Pour chacune, Hune donne le fait, sa source et la personne à contacter.",
  "Ces faits ne se limitent pas aux appels d'offres, aux recrutements ou aux ouvertures : Hune cherche tout ce qui révèle un besoin en rapport avec ce que vend l'entreprise.",
].join("\n");

export const DEFAULT_PROSPECTION_BRIEF = `Expéditeur : une personne de l'équipe Hune, qui signe de son prénom.
Ce que fait Hune :
${HUNE_FACTS}
Promesse du message : un signal réel trouvé pour le destinataire, offert sans contrepartie, choisi d'après ce qu'il vend et les clients qu'il vise.
Appel à l'action : une question qui se répond d'un mot ; la source et les autres signaux partent dans la réponse.`;

// ---------------------------------------------------------------------------
// Boîtes d'envoi
// ---------------------------------------------------------------------------

export type MailboxStatus = "active" | "paused";
export type MailboxPhase = "warmup" | "held" | "active" | "paused";

export const MAILBOX_PHASE_LABELS: Record<MailboxPhase, string> = {
  warmup: "Chauffe",
  held: "Chauffe maintenue",
  active: "Active",
  paused: "En pause",
};

export interface MailboxRecord {
  id: string;
  email: string;
  domain: string;
  display_name: string;
  status: MailboxStatus;
  pause_reason: string | null;
  warmup_started_on: string;
  /** Retenue en chauffe : aucune prospection tant que vrai. */
  warmup_hold: boolean;
  daily_cap: number | null;
  last_sent_at: string | null;
  next_send_at: string | null;
  last_inbox_check_at: string | null;
  access_verified_at: string | null;
  access_error: string | null;
  created_at: string;
  updated_at: string;
}

/** Une boîte telle que l'écran des boîtes la montre : état du jour et santé. */
export interface MailboxOverview extends MailboxRecord {
  phase: MailboxPhase;
  warmupWeek: number;
  capacityToday: number;
  sentToday: number;
  sent7d: number;
  bounces7d: number;
  unsubscribes7d: number;
}

// ---------------------------------------------------------------------------
// E-mails sortants
// ---------------------------------------------------------------------------

export type MessageKind = "sequence" | "reply";

export const MESSAGE_STATUSES = ["to_review", "ready", "held", "sending", "sent", "failed", "cancelled"] as const;
export type MessageStatus = (typeof MESSAGE_STATUSES)[number];

export const MESSAGE_STATUS_LABELS: Record<MessageStatus, string> = {
  to_review: "À valider",
  ready: "Prêt à partir",
  held: "Retenu",
  sending: "Envoi en cours",
  sent: "Envoyé",
  failed: "Échec",
  cancelled: "Annulé",
};

/** Onglets de la file des e-mails (/admin/prospection/envois). */
export const OUTBOX_TABS: Array<{ key: string; label: string; statuses: MessageStatus[] }> = [
  { key: "to_review", label: "À valider", statuses: ["to_review"] },
  { key: "held", label: "Retenus par le garde-fou", statuses: ["held"] },
  { key: "ready", label: "Prêts à partir", statuses: ["ready"] },
  { key: "sent", label: "Envoyés", statuses: ["sent"] },
  { key: "failed", label: "Échecs", statuses: ["failed"] },
];

export const RANK_LABELS: Record<number, string> = {
  1: "Premier e-mail",
  2: "Relance 1",
  3: "Relance 2",
  4: "Relance 3",
};

export type GuardIssue = { severity: "major" | "minor"; code: string; message: string };

export interface GuardOutcome {
  verdict: "pass" | "hold";
  deterministic: GuardIssue[];
  model: GuardIssue[];
  reviewer_failure: string | null;
  reviewer_model: string | null;
  reviewed_at: string;
}

export interface MessageRecord {
  id: string;
  prospect_id: string;
  lead_id: string | null;
  mailbox_id: string | null;
  kind: MessageKind;
  rank: number | null;
  reply_id: string | null;
  machine_subject: string | null;
  machine_body: string | null;
  subject: string | null;
  body: string;
  angle: string | null;
  guard: GuardOutcome | null;
  guard_verdict: "pass" | "hold" | null;
  status: MessageStatus;
  hold_reason: string | null;
  approved_at: string | null;
  approved_auto: boolean;
  edited_at: string | null;
  gmail_message_id: string | null;
  gmail_thread_id: string | null;
  rfc_message_id: string | null;
  sent_at: string | null;
  error: string | null;
  attempts: number;
  locked_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Un e-mail dans la file de relecture, avec de quoi juger sans ouvrir la fiche. */
export interface MessageQueueItem extends MessageRecord {
  prospect: {
    id: string;
    name: string;
    website: string | null;
    contact_name: string | null;
    contact_email: string | null;
    offer_name: string | null;
    priority_segment: string | null;
    status: string;
  } | null;
  lead: {
    id: string;
    organization: string;
    market_clue: string | null;
    observed_on: string | null;
    source_name: string | null;
    source_link: string | null;
    relevance: string | null;
  } | null;
  mailbox: { id: string; email: string; display_name: string } | null;
  /** Texte complet tel qu'il sera reçu (signature et pied compris). */
  preview: string;
}

// ---------------------------------------------------------------------------
// Réponses reçues
// ---------------------------------------------------------------------------

export const REPLY_CLASSES = [
  "yes",
  "question",
  "later",
  "not_interested",
  "unsubscribe",
  "auto_reply",
  "bounce",
  "unclear",
] as const;
export type ReplyClass = (typeof REPLY_CLASSES)[number];

export const REPLY_CLASS_LABELS: Record<ReplyClass, string> = {
  yes: "Oui",
  question: "Question",
  later: "Pas maintenant",
  not_interested: "Pas intéressé",
  unsubscribe: "Désinscription",
  auto_reply: "Réponse automatique",
  bounce: "Rebond",
  unclear: "À trier",
};

export type ReplySource = "rule" | "model" | "default";

export interface ReplyRecord {
  id: string;
  prospect_id: string | null;
  mailbox_id: string | null;
  gmail_message_id: string;
  gmail_thread_id: string | null;
  rfc_message_id: string | null;
  from_email: string | null;
  subject: string | null;
  excerpt: string | null;
  received_at: string;
  class: ReplyClass;
  class_source: ReplySource;
  confidence: number | null;
  corrected_class: ReplyClass | null;
  corrected_at: string | null;
  return_on: string | null;
  after_rank: number | null;
  handled_at: string | null;
  created_at: string;
}

export interface ReplyInboxItem extends ReplyRecord {
  prospect: { id: string; name: string; contact_name: string | null; contact_email: string | null; status: string } | null;
  mailbox: { id: string; email: string; display_name: string } | null;
  /** Réponse préparée (à valider ou déjà partie). */
  answer: MessageRecord | null;
}

/** Classe retenue : la correction humaine prime sur le classement. */
export function effectiveClass(reply: Pick<ReplyRecord, "class" | "corrected_class">): ReplyClass {
  return reply.corrected_class ?? reply.class;
}

// ---------------------------------------------------------------------------
// Passages des crons
// ---------------------------------------------------------------------------

export const PROSPECTION_STEPS = ["prepare", "send", "inbox"] as const;
export type ProspectionStep = (typeof PROSPECTION_STEPS)[number];

export const STEP_LABELS: Record<ProspectionStep, string> = {
  prepare: "Préparation",
  send: "Envoi",
  inbox: "Lecture des boîtes",
};

export type StepReport = {
  status: "done" | "skipped" | "failed";
  message: string;
  error?: string | null;
  counts?: Record<string, number>;
};

export interface RunRecord {
  id: number;
  step: ProspectionStep;
  status: "done" | "failed";
  source: "cron" | "manual";
  started_at: string;
  finished_at: string;
  counts: Record<string, number>;
  message: string | null;
  error: string | null;
}
